import { Activity, Apple, Moon, User, Leaf } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { useDailyVitals } from "@/hooks/use-daily-vitals";
import { SubAreaCard } from "@/components/health/SubAreaCard";

/**
 * /health hub (SPEC 3.3.0). 2x2 grid of sub-area cards on desktop, stacked on
 * mobile. Each card shows a tiny preview pulled from the same hooks the
 * sub-area home pages use.
 */

export function Health() {
  const profile = useAthleteProfile();
  const vitals = useDailyVitals(7);
  const week = useThisWeekSessions();
  const meals = useTodayMealsTotals();

  const latestVitals = vitals.data?.[0];
  const trainingHeadline = week.data
    ? `${week.data.count} session${week.data.count === 1 ? "" : "s"}`
    : null;
  const trainingSub = week.data
    ? `${Math.round(week.data.totalMinutes)}min total · ${week.data.lastSport ?? "no sport tag"}`
    : null;

  const recoveryHeadline = latestVitals?.sleep_min
    ? `Sleep ${formatHours(latestVitals.sleep_min)}`
    : null;
  const recoverySub = latestVitals?.hrv_status_ms
    ? `HRV ${latestVitals.hrv_status_ms.toFixed(0)}ms · ${latestVitals.rhr_bpm ?? "—"}bpm`
    : null;

  const nutritionHeadline = meals.data
    ? `${meals.data.kcal.toFixed(0)} kcal logged`
    : null;
  const nutritionSub = meals.data
    ? `${meals.data.mealCount} meal${meals.data.mealCount === 1 ? "" : "s"} today · P ${meals.data.proteinG.toFixed(0)}g`
    : null;

  const bodyHeadline = profile.data?.current_weight_kg
    ? `${profile.data.current_weight_kg.toFixed(1)} kg`
    : null;
  const bodySub =
    profile.data?.goal_weight_kg && profile.data?.current_weight_kg
      ? `Goal ${profile.data.goal_weight_kg.toFixed(0)} kg · Δ ${(profile.data.current_weight_kg - profile.data.goal_weight_kg).toFixed(1)}kg`
      : profile.data?.current_body_fat_pct
        ? `BF ${profile.data.current_body_fat_pct.toFixed(1)}%`
        : null;

  return (
    <article aria-labelledby="health-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Health
      </p>
      <h1
        id="health-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        How you're holding up.
      </h1>

      <div
        className="haus-health-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "var(--space-4)",
        }}
      >
        <style>{`
          @media (max-width: 767px) {
            .haus-health-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
        <SubAreaCard
          to="/health/training"
          label="Training"
          Icon={Activity}
          headline={trainingHeadline}
          subline={trainingSub}
          loading={week.isLoading}
        />
        <SubAreaCard
          to="/health/nutrition"
          label="Nutrition"
          Icon={Apple}
          headline={nutritionHeadline}
          subline={nutritionSub}
          loading={meals.isLoading}
        />
        <SubAreaCard
          to="/health/recovery"
          label="Recovery"
          Icon={Moon}
          headline={recoveryHeadline}
          subline={recoverySub}
          loading={vitals.isLoading}
        />
        <SubAreaCard
          to="/health/body"
          label="Body"
          Icon={User}
          headline={bodyHeadline}
          subline={bodySub}
          loading={profile.isLoading}
        />
        <SubAreaCard
          to="/health/programs"
          label="Programs"
          Icon={Leaf}
          headline={null}
          subline="Training-block meal sequences"
          loading={false}
        />
      </div>
    </article>
  );
}

function formatHours(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

function useThisWeekSessions() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["health_hub_week_sessions", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const { data, error } = await supabase
        .from("session")
        .select("id, sport_id, duration_min, end_ts")
        .eq("user_id", userId)
        .gte("end_ts", since.toISOString())
        .order("end_ts", { ascending: false });
      if (error) throw error;
      const rows = data ?? [];
      const totalMinutes = rows.reduce((s, r) => s + Number(r.duration_min), 0);
      return {
        count: rows.length,
        totalMinutes,
        lastSport: rows[0]?.sport_id ?? null,
      };
    },
  });
}

function useTodayMealsTotals() {
  const { session } = useSession();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["health_hub_today_meals", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return null;
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      const { data, error } = await supabase
        .from("meal")
        .select("id, kcal, macros")
        .eq("user_id", userId)
        .gte("ts", start.toISOString())
        .lt("ts", end.toISOString());
      if (error) throw error;
      const rows = data ?? [];
      const kcal = rows.reduce((s, r) => s + Number(r.kcal ?? 0), 0);
      let proteinG = 0;
      for (const r of rows) {
        const m = (r.macros as { p?: number } | null) ?? {};
        if (typeof m.p === "number") proteinG += m.p;
      }
      return { mealCount: rows.length, kcal, proteinG };
    },
  });
}
